/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    makeStyles,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { ProjektItem } from '../../models/ProjektItem';
import type { SollIstVergleichItem } from '../../models/SollIstVergleichItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const EXCEL_COLUMNS: ExcelExportColumn<SollIstVergleichItem>[] = [
    { header: 'Position', value: (item) => item.positionBezeichnung?.replace(/<i>|<\/i>/g, ''), width: 45 },
    { header: 'Planned Amount', value: (item) => item.betragPlan ?? 0, width: 18 },
    { header: 'Actual Amount', value: (item) => item.betragIst ?? 0, width: 18 },
    { header: 'Difference', value: (item) => item.differenz ?? 0, width: 18 },
];

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    filterSection: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        alignItems: 'flex-end',
        marginBottom: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        flexWrap: 'wrap',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    exportButton: {
        display: 'flex',
        alignItems: 'flex-end',
        marginBottom: 0,
    },
    filterLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    tableRowWithGap: {
        marginBottom: tokens.spacingVerticalL,
    },
    boldRow: {
        fontWeight: 600,
    },
    boldRow2: {
        fontWeight: 600,
        textAlign: 'right',
    },
    separatorRow: {
        borderTop: `2px solid ${tokens.colorNeutralStroke2}`,
        '& td': {
            padding: '2px 0',
            height: '0',
            lineHeight: '0',
        },
    },
    totalRow: {
        fontWeight: 700,
        backgroundColor: tokens.colorNeutralBackground2,
        fontSize: tokens.fontSizeBase300,
    },
    tableHeader: {
        fontWeight: 700,
        backgroundColor: tokens.colorNeutralBackground2,
        color: tokens.colorNeutralForeground1,
        borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    },
    selectWidth: {
        minWidth: '200px',
    },
    dateInputWidth: {
        minWidth: '180px',
    },
    rightAlignedCell: {
        textAlign: 'right',
    },
    rightAlignedLayout: {
        justifyContent: 'flex-end',
    },
    rightAlignedHeader: {
        paddingLeft: '0 !important',
        paddingRight: tokens.spacingHorizontalL,
    },
});

interface SollIstVergleichProps {
    project: ProjektItem | null;
}

interface TimeframeOption {
    label: string;
    value: string;
}

// Helper functions
function parseDate(dateValue: any): Date | null {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    // Check if date is valid and not at epoch
    if (isNaN(date.getTime())) return null;
    return date;
}

function isValidDate(date: Date | null): boolean {
    if (!date) return false;
    const time = date.getTime();
    // Check if date is valid and not at epoch (year 1970)
    return !isNaN(time) && time > 0;
}

function getProjektstart(projekt: ProjektItem | null): Date {
    if (!projekt) return new Date();

    // Try operative start (actual)
    let date = parseDate((projekt as any).operativerBeginnIst);
    if (isValidDate(date)) return date!;

    // Try operative start (planned)
    date = parseDate((projekt as any).operativerBeginnPlan);
    if (isValidDate(date)) return date!;

    // Fallback to today
    return new Date();
}

function getProjektende(projekt: ProjektItem | null): Date {
    if (!projekt) return new Date();

    // Try kaufmaennisches (commercial) end
    let date = parseDate((projekt as any).kaufmaennischesEnde);
    if (isValidDate(date)) return date!;

    // Try operative end (actual)
    date = parseDate((projekt as any).operativesEndeIst);
    if (isValidDate(date)) return date!;

    // Try operative end (planned)
    date = parseDate((projekt as any).operativesEndePlan);
    if (isValidDate(date)) return date!;

    // Fallback to today
    return new Date();
}

function getWirtschaftsjahr(datum: Date): string {
    const monat = datum.getMonth() + 1; // getMonth returns 0-11, we need 1-12
    const jahr = datum.getFullYear();

    if (monat >= 9) {
        const nextYear = (jahr + 1) % 100;
        return `WJ ${jahr}/${String(nextYear).padStart(2, '0')}`;
    } else {
        const prevYear = jahr - 1;
        const currYearSuffix = String(jahr % 100).padStart(2, '0');
        return `WJ ${prevYear}/${currYearSuffix}`;
    }
}

function generateWirtschaftsjahrOptions(start: Date, end: Date): TimeframeOption[] {
    const options: TimeframeOption[] = [];
    const wirtschaftsjahre = new Set<string>();

    const current = new Date(start);
    while (current <= end) {
        const wj = getWirtschaftsjahr(current);
        if (!wirtschaftsjahre.has(wj)) {
            wirtschaftsjahre.add(wj);
            // Remove only the space, keep the slash: 'WJ 2025/26' -> 'WJ2025/26'
            options.push({
                label: wj,
                value: wj.replace(/\s/g, ''),
            });
        }
        current.setMonth(current.getMonth() + 1);
    }

    return options;
}

const SollIstVergleich: React.FC<SollIstVergleichProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [vergleichData, setVergleichData] = useState<SollIstVergleichItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedOption, setSelectedOption] = useState<string>('gesamt');
    const [schemaId, setSchemaId] = useState<number>(2);
    const [dateVon, setDateVon] = useState<Date | undefined>(undefined);
    const [dateBis, setDateBis] = useState<Date | undefined>(undefined);
    const [timeframeOptions, setTimeframeOptions] = useState<TimeframeOption[]>([
        { label: 'Total', value: 'gesamt' },
        { label: 'Projektstart bis heute', value: 'startBisHeute' },
        { label: 'Benutzerdefinierter Zeitraum', value: 'custom' },
    ]);
    const [projektStart, setProjektStart] = useState<Date | null>(null);
    const [projektEnd, setProjektEnd] = useState<Date | null>(null);
    const [berichtstermine, setBerichtstermine] = useState<any[]>([]);

    // Create dataService instance using MSAL context
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch vergleich data
    const fetchVergleichData = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            let data: SollIstVergleichItem[];
            let startDate: Date | undefined;
            let endDate: Date | undefined;

            if (selectedOption === 'gesamt') {
                // Total - no date filtering
                data = await dataService.getSollIstVergleichListe(project.projektID, schemaId);
            } else if (selectedOption === 'custom') {
                // Custom date range
                if (!dateVon || !dateBis) {
                    setError('Please select both "From" and "To" dates');
                    setVergleichData([]);
                    setIsLoading(false);
                    return;
                }
                startDate = dateVon;
                endDate = dateBis;
                data = await dataService.getSollIstVergleichListeZeitraum(
                    project.projektID,
                    startDate,
                    endDate,
                    schemaId
                );
            } else if (selectedOption.startsWith('jahr_')) {
                // Calendar year (e.g., 'jahr_2026')
                const year = parseInt(selectedOption.replace('jahr_', ''), 10);
                startDate = new Date(year, 0, 1);
                endDate = new Date(year, 11, 31);
                data = await dataService.getSollIstVergleichListeZeitraum(
                    project.projektID,
                    startDate,
                    endDate,
                    schemaId
                );
            } else if (selectedOption.startsWith('WJ')) {
                // Wirtschaftsjahr (fiscal year, Sept 1 - Aug 31)
                // Parse the value like 'WJ2025/26' to get 2025 as start year
                const yearPart = selectedOption.replace('WJ', ''); // '2025/26'
                const startYear = parseInt(yearPart.split('/')[0], 10);
                // Fiscal year runs from Sept 1 to Aug 31
                startDate = new Date(startYear, 8, 1); // September 1
                endDate = new Date(startYear + 1, 7, 31); // August 31 of next year
                data = await dataService.getSollIstVergleichListeZeitraum(
                    project.projektID,
                    startDate,
                    endDate,
                    schemaId
                );
            } else if (selectedOption === 'startBisHeute') {
                // From project start to today
                if (!projektStart) {
                    setError('Project start date not available');
                    setVergleichData([]);
                    setIsLoading(false);
                    return;
                }
                startDate = projektStart;
                endDate = new Date();
                data = await dataService.getSollIstVergleichListeZeitraum(
                    project.projektID,
                    startDate,
                    endDate,
                    schemaId
                );
            } else if (selectedOption.startsWith('report_')) {
                // Report date - extract datumVon and datumBis from the selected report
                const reportId = selectedOption.replace('report_', '');
                const selectedReport = berichtstermine.find(r => r.value === reportId);

                if (!selectedReport) {
                    setError('Selected report not found');
                    setVergleichData([]);
                    setIsLoading(false);
                    return;
                }

                const parsedStartDate = parseDate(selectedReport.datumVon);
                const parsedEndDate = parseDate(selectedReport.datumBis);

                if (!isValidDate(parsedStartDate) || !isValidDate(parsedEndDate)) {
                    setError('Report dates are invalid');
                    setVergleichData([]);
                    setIsLoading(false);
                    return;
                }

                startDate = parsedStartDate!;
                endDate = parsedEndDate!;

                data = await dataService.getSollIstVergleichListeZeitraum(
                    project.projektID,
                    startDate,
                    endDate,
                    schemaId
                );
            } else {
                setError('Invalid timeframe option selected');
                setVergleichData([]);
                setIsLoading(false);
                return;
            }
            setVergleichData(data || []);
        } catch (err) {
            console.error('Error loading vergleich data:', err);
            setError('Error loading comparison data');
            setVergleichData([]);
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService, selectedOption, schemaId, dateVon, dateBis, projektStart, berichtstermine]);

    // Initialize timeframe options on project load
    useEffect(() => {
        if (!project) return;

        const start = getProjektstart(project);
        const end = getProjektende(project);

        console.log('Project dates:', { start, end, startYear: start.getFullYear(), endYear: end.getFullYear() });
        console.log('Project object dates:', {
            operativerBeginnIst: (project as any).operativerBeginnIst,
            operativerBeginnPlan: (project as any).operativerBeginnPlan,
            kaufmaennischesEnde: (project as any).kaufmaennischesEnde,
            operativesEndeIst: (project as any).operativesEndeIst,
            operativesEndePlan: (project as any).operativesEndePlan,
        });

        setProjektStart(start);
        setProjektEnd(end);

        // Set min/max dates for date pickers
        setDateVon(start);
        setDateBis(end);

        // Generate calendar years
        const calendarYears: TimeframeOption[] = [];
        for (let jahr = start.getFullYear(); jahr <= end.getFullYear(); jahr++) {
            calendarYears.push({
                label: jahr.toString(),
                value: `jahr_${jahr}`,
            });
        }

        // Generate wirtschaftsjahre
        const wirtschaftsjahreOptions = generateWirtschaftsjahrOptions(start, end);

        // Combine all options (we'll also add report dates and other options below)
        const options: TimeframeOption[] = [
            { label: 'Total', value: 'gesamt' },
            ...calendarYears,
            ...wirtschaftsjahreOptions,
            // Report dates will be added by another effect
            { label: 'Projektstart bis heute', value: 'startBisHeute' },
            { label: 'Benutzerdefinierter Zeitraum', value: 'custom' },
        ];

        setTimeframeOptions(options);
        console.log('Initialized timeframe options:', options);
    }, [project]);

    // Fetch report dates and add them to options
    useEffect(() => {
        if (!project?.projektID || !dataService) return;

        const fetchReportDates = async () => {
            try {
                const berichtstermine = await dataService.getBerichtsterminListe(project.projektID);
                console.log('Fetched report dates:', berichtstermine);
                if (berichtstermine && Array.isArray(berichtstermine)) {
                    // Store the reports in state for use in fetchVergleichData
                    setBerichtstermine(berichtstermine);

                    setTimeframeOptions(prev => {
                        // First, remove any existing report options from previous state
                        const withoutReports = prev.filter(opt => !opt.value.startsWith('report_'));

                        // Create one option for each Berichtstermin
                        const reportOptions = (berichtstermine as any[]).map(item => ({
                            label: item.text || item.label,
                            value: `report_${item.value}`,
                        }));

                        // Insert report options before "Projektstart bis heute"
                        const startBisHeuteIndex = withoutReports.findIndex(opt => opt.value === 'startBisHeute');
                        if (startBisHeuteIndex !== -1) {
                            return [...withoutReports.slice(0, startBisHeuteIndex), ...reportOptions, ...withoutReports.slice(startBisHeuteIndex)];
                        }
                        return withoutReports;
                    });
                }
            } catch (err) {
                console.error('Error fetching report dates:', err);
            }
        };

        fetchReportDates();
    }, [project?.projektID, dataService]);

    useEffect(() => {
        fetchVergleichData();
    }, [selectedOption, schemaId, fetchVergleichData]);

    if (!project) return null;

    return (
        <div className={styles.container}>
            <div className={styles.filterSection}>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Timeframe</label>
                    <Select
                        value={selectedOption}
                        onChange={(_e, data) => {
                            setSelectedOption(data.value);
                        }}
                        className={styles.selectWidth}
                    >
                        {timeframeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>

                {selectedOption === 'custom' && (
                    <>
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>From</label>
                            <DatePicker
                                value={dateVon}
                                onSelectDate={(date: Date | null | undefined) => setDateVon(date ?? undefined)}
                                placeholder="Select from date"
                                className={styles.dateInputWidth}
                                minDate={projektStart || undefined}
                                maxDate={projektEnd || undefined}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>To</label>
                            <DatePicker
                                value={dateBis}
                                onSelectDate={(date: Date | null | undefined) => setDateBis(date ?? undefined)}
                                placeholder="Select to date"
                                className={styles.dateInputWidth}
                                minDate={projektStart || undefined}
                                maxDate={projektEnd || undefined}
                            />
                        </div>
                    </>
                )}

                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Schema</label>
                    <Select
                        value={schemaId.toString()}
                        onChange={(_e, data) => {
                            setSchemaId(parseInt(data.value, 10));
                        }}
                        className={styles.selectWidth}
                    >
                        <option value="1">4005 (BIS)</option>
                        <option value="2">5000 (IMCProjects)</option>
                    </Select>
                </div>


                <ExcelExportButton
                    items={isLoading ? [] : vergleichData}
                    columns={EXCEL_COLUMNS}
                    fileName={`Soll-Ist-Vergleich_${project.projektID}_${new Date().toISOString().split('T')[0]}`}
                    sheetName="Soll-Ist Vergleich"
                />
            </div>

            {error && (
                <div className={styles.emptyState} style={{ color: tokens.colorStatusDangerForeground1 }}>
                    {error}
                </div>
            )}

            {!error && vergleichData.length === 0 && !isLoading && (
                <div className={styles.emptyState}>
                    No comparison data available
                </div>
            )}

            {!isLoading ? <div className={styles.tableContainer}>
                <Table
                    size="small"
                >
                    <TableHeader>
                        <TableRow style={{ backgroundColor: tokens.colorNeutralBackground2, borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                            <TableHeaderCell>
                                <div style={{ fontWeight: 700 }}>Position</div>
                            </TableHeaderCell>
                            <TableHeaderCell><div style={{ textAlign: 'right', width: '100%', fontWeight: 700 }}>Planned Amount</div></TableHeaderCell>
                            <TableHeaderCell><div style={{ textAlign: 'right', width: '100%', fontWeight: 700 }}>Actual Amount</div></TableHeaderCell>
                            <TableHeaderCell><div style={{ textAlign: 'right', width: '100%', fontWeight: 700 }}>Difference</div></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {vergleichData.map((item, index) => {
                            const isLastRow = index === vergleichData.length - 1;
                            const isSpecialRow = schemaId === 2 && (index === 0 || index === 1 || index === 4 || isLastRow);
                            const positionText = isSpecialRow
                                ? item.positionBezeichnung.replace(/<i>|<\/i>/g, '')
                                : item.positionBezeichnung;

                            // Apply only one class based on conditions - don't concatenate Fluent UI styles
                            const rowClassNames = isSpecialRow ? styles.totalRow : (item.isSeparator ? undefined : styles.tableRow);

                            return (
                                <React.Fragment key={index}>
                                    <TableRow className={rowClassNames}>
                                        <TableCell>
                                            <TableCellLayout className={isSpecialRow ? styles.boldRow : undefined}>
                                                {positionText}
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell>
                                            <TableCellLayout className={isSpecialRow ? styles.boldRow2 : undefined} style={{ justifyContent: 'flex-end' }}>
                                                {item.betragPlanAsText || item.betragPlan?.toFixed(2) || '0.00'}
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell>
                                            <TableCellLayout className={isSpecialRow ? styles.boldRow2 : undefined} style={{ justifyContent: 'flex-end' }}>
                                                {item.betragIstAsText || item.betragIst?.toFixed(2) || '0.00'}
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell>
                                            <TableCellLayout className={isSpecialRow ? styles.boldRow2 : undefined} style={{ justifyContent: 'flex-end' }}>
                                                {item.differenzAsText || item.differenz?.toFixed(2) || '0.00'}
                                            </TableCellLayout>
                                        </TableCell>
                                    </TableRow>
                                    {schemaId === 2 && (index === 0 || index === 1 || index === 4) && !isLastRow && (
                                        <TableRow className={styles.separatorRow}>
                                            <TableCell>
                                                <TableCellLayout></TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout></TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout></TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout></TableCellLayout>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
                :
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading comparison data..." />
                </div>
            }
        </div>
    );
};

export default SollIstVergleich;
